import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";

const TrackingEvents = {
  ASC_COMM_ENGAGEMENT: "asc_comm_engagement",
  ASC_COMM_SUBMISSION: "asc_comm_submission",
  ASC_FORM_ENGAGEMENT: "asc_form_engagement",
  ASC_FORM_SUBMISSION: "asc_form_submission",
  ASC_CTA_INTERACTION: "asc_cta_interaction",
};

const transformCategory = (itemCategory) => {
  if (itemCategory === "new" || itemCategory === "used") {
    return "sales";
  }
  return itemCategory;
};

const formatTrackingNumber = (trackingNumber) => {
  if (!trackingNumber) return [];
  return trackingNumber
    .split(/[,\s]+/)
    .map((id) => id.trim())
    .filter((id) => id);
};

const useAnalytics = (trackingNumber, onlyCustomEmitEvent = false) => {
  const location = useLocation();
  const isClient =
    typeof window !== "undefined" && typeof document !== "undefined";
  const [isInitialized, setIsInitialized] = useState(false);
  const formEngagedWidgets = useRef(new Set());
  const ctaInteractedWidgets = useRef(new Set());
  const engagementTimeouts = useRef([]);

  const emitCustomEvent = useCallback(
    (eventData) => {
      if (!isClient || !onlyCustomEmitEvent) return;

      const customEvent = {
        type: "matador_analytics_event",
        timestamp: new Date().toISOString(),
        gaId: trackingNumber,
        ...eventData,
      };

      if (window.parent && window.parent !== window) {
        window.parent.postMessage(customEvent, "*");
      }
    },
    [isClient, onlyCustomEmitEvent]
  );

  const loadGtagScript = useCallback(
    (measurementId) => {
      if (!isClient || window.gtag || onlyCustomEmitEvent) return;
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
      window.gtag("js", new Date());
    },
    [isClient, onlyCustomEmitEvent]
  );

  const initGtag = useCallback(
    (id, options) => {
      if (!isClient || onlyCustomEmitEvent) return;
      if (!window.gtag) loadGtagScript(id);

      const configOpts = {
        cookie_flags: options.cookieFlags,
        send_page_view: options.sendPageView,
      };

      window.gtag("config", id, configOpts);
    },
    [isClient, loadGtagScript, onlyCustomEmitEvent]
  );

  useEffect(() => {
    if (!isClient || !trackingNumber) return;

    if (onlyCustomEmitEvent) {
      setIsInitialized(true);
      return;
    }

    const ids = Array.from(new Set(formatTrackingNumber(trackingNumber)));
    if (ids.length === 0) return;

    ids.forEach((id, index) => {
      if (index === 0) {
        loadGtagScript(id);
      }
      initGtag(id, {
        cookieFlags: "SameSite=None;Secure",
        sendPageView: false,
      });
    });

    setIsInitialized(true);

    return () => {
      engagementTimeouts.current.forEach(clearTimeout);
      engagementTimeouts.current = [];
    };
  }, [
    isClient,
    trackingNumber,
    location.search,
    loadGtagScript,
    initGtag,
    onlyCustomEmitEvent,
  ]);

  const sendEvent = useCallback(
    ({ name, widget, status, itemCategory }) => {
      if (!isClient || !isInitialized) return;

      const eventData = {
        name,
        comm_type: widget,
        event_owner: "matador",
        ...(status ? { status } : {}),
        ...(itemCategory ? { item_category: itemCategory } : {}),
      };

      if (onlyCustomEmitEvent) {
        emitCustomEvent(eventData);
        return;
      }

      if (window.gtag) {
        window.gtag("event", name, eventData);
      }
    },
    [isClient, isInitialized, onlyCustomEmitEvent, emitCustomEvent]
  );

  const sendCommEngagementEvent = useCallback(
    ({ widget, status = "start" }) => {
      if (!isInitialized) return;
      const timeoutId = setTimeout(() => {
        sendEvent({ name: TrackingEvents.ASC_COMM_ENGAGEMENT, widget, status });
      }, 2000);
      engagementTimeouts.current.push(timeoutId);
    },
    [isInitialized, sendEvent]
  );

  const sendCommSubmissionEvent = useCallback(
    ({ widget, status = "start", itemCategory }) => {
      const category = transformCategory(itemCategory);
      sendEvent({
        name: TrackingEvents.ASC_COMM_SUBMISSION,
        widget,
        status,
        itemCategory: category,
      });
    },
    [sendEvent]
  );

  const sendFormEngagementEvent = useCallback(
    ({ widget }) => {
      if (!formEngagedWidgets.current.has(widget)) {
        formEngagedWidgets.current.add(widget);
        sendEvent({ name: TrackingEvents.ASC_FORM_ENGAGEMENT, widget });
      }
    },
    [sendEvent]
  );

  const sendFormSubmissionEvent = useCallback(
    ({ widget }) => {
      sendEvent({ name: TrackingEvents.ASC_FORM_SUBMISSION, widget });
    },
    [sendEvent]
  );

  const sendCtaInteractionEvent = useCallback(
    ({ widget }) => {
      if (!ctaInteractedWidgets.current.has(widget)) {
        ctaInteractedWidgets.current.add(widget);
        sendEvent({ name: TrackingEvents.ASC_CTA_INTERACTION, widget });
      }
    },
    [sendEvent]
  );

  const d2cUtmTracking = (locationId) => {
    const d2cLocationIds = [
      "66423a2154c0270013a7d589",
      "6671c35e39b7f088eca4ef9c",
    ];
    if (d2cLocationIds.includes(locationId)) {
      window.parent.postMessage("userProvidedContactInfod2c", "*");
    }
  };

  return {
    sendCommEngagementEvent,
    sendCommSubmissionEvent,
    sendFormEngagementEvent,
    sendFormSubmissionEvent,
    sendCtaInteractionEvent,
    d2cUtmTracking,
    isInitialized,
  };
};

export default useAnalytics;
